import TextContent from '@amzn/awsui-components-react/polaris/text-content';
import * as awsui from '@amzn/awsui-design-tokens/polaris';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { SHORTBREAD_BUTTON_ID } from '../constants/cookies';

import footerMessages from '../i18n/footer.messages';
import globalMessages from '../i18n/global.messages';
import { getShortbread } from '../services/cookies';
import styles from './Footer.module.css';
import ScreenReaderHidden from './ScreenReaderHidden';
import React from 'react';

type FooterProps = {
  enableCookieComponent?: boolean;
};

const footerStyle = {
  backgroundColor: awsui.colorBackgroundLayoutMain,
};

const whatYearIsIt = new Date().getFullYear();

const PipeSpacer = () => (
  <span className={styles.pipeSpacer} aria-hidden="true">
    |
  </span>
);

const ExternalLinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ marginLeft: '4px', verticalAlign: 'middle' }}
  >
    <path
      d="M13 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4M9 2h5v5M7 9l6-6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FooterItem = ({ children }: { children: ReactNode }) => (
  <span>{children}</span>
);

const CookiePreferencesButton = ({ children }: { children: ReactNode }) => {
  const handleCustomizeCookies = () => {
    const shortbread = getShortbread();
    shortbread.customizeCookies();
  };
  return (
    <a
      id={SHORTBREAD_BUTTON_ID}
      href="#cookiePreferences"
      role="button"
      aria-haspopup="dialog"
      onClick={(event) => {
        event.preventDefault();
        handleCustomizeCookies();
      }}
      onKeyDown={(event) => {
        if (event.key === ' ') event.preventDefault();
      }}
      onKeyUp={(event) => {
        if (event.key === ' ') {
          event.preventDefault();
          handleCustomizeCookies();
        }
      }}
    >
      {children}
    </a>
  );
};
const Footer = (props: FooterProps) => {
  const cookieComponent = () => {
    if (props.enableCookieComponent === true) {
      return (
        <>
          <PipeSpacer />
          <li>
            {' '}
            <FooterItem>
              <CookiePreferencesButton>
                {formatMessage(footerMessages.cookiePreferences, {
                  i: (str) => <i>{str}</i>,
                })}
              </CookiePreferencesButton>
            </FooterItem>
          </li>
        </>
      );
    }
    return undefined;
  };
  const { formatMessage } = useIntl();

  const privacyLink = (
    <FooterItem>
      <a
        href="https://aws.amazon.com/privacy/"
        target="_blank"
        rel="noopener noreferrer"
      >
        {formatMessage(footerMessages.privacyLink)}
        <ExternalLinkIcon />
        <ScreenReaderHidden>
          {formatMessage(globalMessages.opensInNewTab)}
        </ScreenReaderHidden>
      </a>
    </FooterItem>
  );

  const siteTermsLink = (
    <FooterItem>
      <a
        href="https://aws.amazon.com/terms/"
        target="_blank"
        rel="noopener noreferrer"
      >
        {formatMessage(footerMessages.siteTermsLink)}
        <ExternalLinkIcon />
        <ScreenReaderHidden>
          {formatMessage(globalMessages.opensInNewTab)}
        </ScreenReaderHidden>
      </a>
    </FooterItem>
  );

  const copyrightInformation = (
    <FooterItem>
      {formatMessage(footerMessages.copyrightInformation, {
        year: whatYearIsIt,
      })}
    </FooterItem>
  );

  return (
    <TextContent>
      <footer className={styles.footer} style={footerStyle} role="contentinfo">
        <ul className={styles.horizontal_list}>
          <li>{privacyLink}</li>
          <PipeSpacer />
          <li>{siteTermsLink}</li>
          {cookieComponent()}
          <PipeSpacer />
          <li>{copyrightInformation}</li>
        </ul>
      </footer>
    </TextContent>
  );
};

export default Footer;
